import React, { useState, useRef } from 'react';
import { TRPCClientErrorLike } from '@trpc/client';
import { DefaultErrorShape } from '@trpc/server/unstable-core-do-not-import';
import { Tooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons/faInfoCircle';
import { TypedMemo } from '../../../common/types';
import { formatNumber } from '../../utils/formatters';
import { PercentageChangeElement } from '../PositionChangeElement';
import { LoadingView } from '../../../common/views/LoadingView';
import { useChartTokens } from '../../theme/useChartTokens';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';

interface StatsData {
  current: number;
  previous: number | null;
  percentageChange: number | null;
}

interface ArtistStatsCardProps {
  title: string;
  data?: StatsData;
  isLoading?: boolean;
  error?: TRPCClientErrorLike<{ transformer: true; errorShape: DefaultErrorShape }> | null;
  minWidth?: string;
  unit?: string; // e.g., "times", "score", "points"
  date?: string; // Optional date to display
  /**
   * When true, inverts the color logic for percentage changes:
   * - Positive changes become red (bad)
   * - Negative changes become green (good)
   * Use this for metrics where decrease is desirable (e.g., "Playlist Loss Score")
   */
  invertColorLogic?: boolean;
  /**
   * Tooltip text to display when hovering over the info icon
   * If not provided, no info icon will be shown
   */
  tooltipText?: string;
}

const ArtistStatsCardComponent: React.FC<ArtistStatsCardProps> = ({
  title,
  data,
  isLoading,
  error,
  minWidth = '180px',
  unit = 'times',
  date,
  invertColorLogic = false,
  tooltipText,
}) => {
  const chart = useChartTokens();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);
  const tooltipIdRef = useRef(`stats-tooltip-${Math.random().toString(36).slice(2, 9)}`);
  const tooltipId = tooltipIdRef.current;
  return (
    <Panel className="mt-3 d-flex flex-column" style={{ minWidth }}>
      <PanelHead
        title={title}
        subtitle={date}
        right={
          tooltipText ? (
            <>
              <span id={tooltipId} style={{ cursor: 'pointer', color: chart.tx2 }}>
                <FontAwesomeIcon icon={faInfoCircle} size="sm" />
              </span>
              <Tooltip placement="top" isOpen={tooltipOpen} target={tooltipId} toggle={toggleTooltip}>
                {tooltipText}
              </Tooltip>
            </>
          ) : null
        }
      />
      <PanelBody className="d-flex flex-grow-1 align-items-center justify-content-center">
        {isLoading === true ? (
          <LoadingView />
        ) : error || !data ? (
          <div className="text-center" style={{ fontSize: '14px', color: chart.neg }}>
            Error
          </div>
        ) : (
          <div className="d-flex align-items-center" style={{ gap: '16px' }}>
            <div className="text-center">
              <div style={{ fontSize: '16px', color: chart.tx0, fontWeight: 400 }}>{formatNumber(data.current)}</div>
              <div style={{ fontSize: '10px', color: chart.tx2 }}>{unit}</div>
            </div>
            <PercentageChangeElement percentageChange={data.percentageChange} invertColorLogic={invertColorLogic} />
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const ArtistStatsCard = TypedMemo(ArtistStatsCardComponent);
